/**
 * Pure builder: hydrated `Sheet` → `ParsedTree` snapshot.
 *
 * Mirrors the `hydrated` field of a short-format fixture
 * (TREE-SPEC §2): walks `sheet.tree.roots()` + `sheet.tree.children`
 * to emit one row node per `ModelRow`, preserving wire type
 * (`wireTypeOf`), label, and the row's `attributes()` bag.
 *
 * The output round-trips cleanly through `serializeTree` /
 * `parseTree` (TREE-SPEC §8). Diffing two snapshots yields the
 * `TreeDiff` that the reactive lona-js extension will pipe to a
 * renderer (TREE-SPEC §10).
 *
 * Pure function — no IO, no async, no scene/render dependencies.
 * The reactive wrapper (subscribing to invalidation events and
 * calling `buildHydratedTree` on change) lives in lona-js per the
 * spec's layering rule.
 */

import type { Option } from "../core/option.ts";
import type { CellNode, RenderScene } from "./scene.ts";
import { type ParsedTree } from "../raw/parse-tree.ts";
import { wireTypeOf } from "../raw/raw-sheet.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { SheetModelReads } from "../sheet/rows-sheet-view.ts";
import { type ModelRowReads } from "../rows/model-row-reads.ts";
import { liveRowOf } from "../rows/model-row.ts";
import type { PerTypeSpec } from "../plugins/per-type-spec.ts";
import type { RenderPluginLookup } from "./render-plugin-core.ts";
import { RenderBinding } from "./render-plugin-core.ts";

const ROOT_NAME = "sheet";

/**
 * Build a `ParsedTree` mirroring the hydrated row hierarchy of
 * `sheet`. Root carries `name: "sheet"`; each row becomes a `row`
 * node with `type` from `wireTypeOf`, `label` from `row.label`,
 * and `attrs` projected from `row.attributes()`.
 *
 * Attributes that aren't plain strings/numbers/booleans (objects
 * like `props`) flatten to dotted keys (`props.locationId`) so the
 * serialized output matches the existing fixture grammar.
 */
function buildHydratedTree(sheet: SheetModelReads): ParsedTree {
  const root: ParsedTree = {
    kind: "root",
    name: ROOT_NAME,
    children: [],
  };
  for (const rootId of sheet.rootIds()) {
    root.children.push(buildRowNode(sheet, rootId));
  }
  return root;
}

function buildRowNode(sheet: SheetModelReads, id: RowLocalId): ParsedTree {
  const row = sheet.peekRow(id);
  if (!row) {
    throw new Error(`buildHydratedTree: row ${id} not found in sheet`);
  }
  const node: ParsedTree = {
    kind: "row",
    type: wireTypeOf(row),
    attrs: flattenAttributes(row.attributes() ?? {}),
    label: row.label,
    children: [],
  };
  for (const childId of sheet.childrenOf(id)) {
    node.children.push(buildRowNode(sheet, childId));
  }
  return node;
}

/**
 * Project a row's attribute bag into the flat `Record<string, string>`
 * the serializer expects. Nested objects flatten to dotted keys
 * (e.g. `{ props: { locationId: "x" } }` → `{ "props.locationId": "x" }`).
 *
 * Each value renders as its raw text form:
 *   - string → `value` (no quotes added; serializer-side quoting is
 *     left to caller decisions if a comma/`]` would otherwise break
 *     the strict grammar; today's wire data doesn't trigger this)
 *   - number / boolean → coerced via `String()`
 *   - null / undefined → entry omitted
 *   - other values (arrays, lisp-like) → `JSON.stringify` for
 *     opacity preservation
 */
function flattenAttributes(
  attrs: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  walk(attrs, "", out);
  return out;
}

function walk(
  obj: Record<string, unknown>,
  prefix: string,
  out: Record<string, string>,
): void {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix === "" ? k : `${prefix}.${k}`;
    if (v == null) continue;
    if (typeof v === "string") {
      out[key] = v;
    } else if (typeof v === "number" || typeof v === "boolean") {
      out[key] = String(v);
    } else if (Array.isArray(v)) {
      out[key] = JSON.stringify(v);
    } else if (typeof v === "object") {
      walk(v as Record<string, unknown>, key, out);
    } else {
      out[key] = String(v);
    }
  }
}

// ─── Scene-aware tree builder (Phase 7) ───────────────────────────

/**
 * Pure builder: hydrated `Sheet` + `RenderScene` → `ParsedTree`
 * with cell children. Mirrors `buildHydratedTree` for the row
 * hierarchy, then attaches one cell node per drawn cell as a
 * deeper child of its row (TREE-SPEC §3.4).
 *
 * Cell payload:
 * - `columnLabel` from `cell.columnDate` formatted by
 *   `formatColumnLabel` (defaults to ISO `YYYY-MM-DD`; callers
 *   can override).
 * - `value` from `cell.content?.value.entry?.value` stringified.
 * - `glyph` left undefined here; reactive renderers append glyphs
 *   based on sync state / formula attribution etc.
 *
 * Pure: no IO, no async. The reactive wrapper (e.g. `TreeWatcher`)
 * runs this inside its `snapshot()` if scene-aware output is needed.
 */
function buildSceneTree(
  sheet: SheetModelReads,
  scene: RenderScene,
  options: BuildSceneTreeOptions = {},
): ParsedTree {
  const cellsByRow = groupCellsByRow(scene);
  const formatLabel = options.formatColumnLabel ?? defaultColumnLabel;
  const root: ParsedTree = {
    kind: "root",
    name: "sheet",
    children: [],
  };
  for (const rootId of sheet.rootIds()) {
    root.children.push(
      buildRowWithCells(sheet, rootId, cellsByRow, formatLabel, options.registry ?? null),
    );
  }
  return root;
}

/** Options for `SceneTree.build`, such as the per-cell column-label formatter. */
export interface BuildSceneTreeOptions {
  /**
   * Override the per-cell columnLabel formatter. Default emits
   * `YYYY-MM-DD` (matches the strict NaiveDate.toString format used
   * across the corpus). The TUI design proposes richer labels like
   * `Mon Apr 21` for day-scale, `Week 17` for week-scale — callers
   * can supply those.
   */
  readonly formatColumnLabel?: (cell: CellNode) => string;
  /**
   * Optional plugin registry for resolving per-cell `cellValueFormatter`
   * from `PerTypeSpec`. When provided, the scene tree's cell values
   * route through `spec.cellValueFormatter(value, ctx)` so CLI and
   * DOM share one stringification path. When omitted, falls back to
   * the built-in `stringifyCellValue` switch (legacy behavior).
   */
  readonly registry?: RenderPluginLookup;
}

function buildRowWithCells(
  sheet: SheetModelReads,
  id: RowLocalId,
  cellsByRow: ReadonlyMap<string, readonly CellNode[]>,
  formatLabel: (cell: CellNode) => string,
  registry: Option<RenderPluginLookup>,
): ParsedTree {
  const row = sheet.peekRow(id);
  if (!row) {
    throw new Error(`buildSceneTree: row ${id} not found in sheet`);
  }
  const node: ParsedTree = {
    kind: "row",
    type: wireTypeOf(row),
    attrs: flattenAttributes(row.attributes() ?? {}),
    label: row.label,
    children: [],
  };
  // Attach cells (sorted by columnIndex) under the row.
  const rowCells = cellsByRow.get(id.toString()) ?? [];
  const spec = registry ? resolveCellSpec(row, registry) : null;
  for (const cell of rowCells) {
    node.children.push({
      kind: "cell",
      columnLabel: formatLabel(cell),
      value: stringifyCellValue(cell, spec),
      children: [],
    });
  }
  // Recurse into row children (alias / renderer / nested rows).
  for (const childId of sheet.childrenOf(id)) {
    node.children.push(
      buildRowWithCells(sheet, childId, cellsByRow, formatLabel, registry),
    );
  }
  return node;
}

/**
 * Resolve the renderer/layout plugin spec that owns this row's cell.
 * Returns null when binding resolution fails (rare — typically for
 * synthetic test rows). Used by `buildSceneTree` to look up
 * `cellValueFormatter`.
 */
function resolveCellSpec(
  row: ModelRowReads,
  registry: RenderPluginLookup,
): Option<PerTypeSpec> {
  // The reads a tree is built from may hand out a row's reads face; the
  // binding resolver reads its variant, so it takes the live row.
  const live = liveRowOf(row);
  try {
    const binding = RenderBinding.resolveCell(live, registry);
    return registry.get(binding.pluginId, binding.pluginRole) as PerTypeSpec;
  } catch {
    return null;
  }
}

function groupCellsByRow(
  scene: RenderScene,
): ReadonlyMap<string, readonly CellNode[]> {
  const grouped = new Map<string, CellNode[]>();
  for (const cell of scene.cells.values()) {
    const key = cell.key.row.toString();
    let arr = grouped.get(key);
    if (!arr) {
      arr = [];
      grouped.set(key, arr);
    }
    arr.push(cell);
  }
  for (const arr of grouped.values()) {
    arr.sort((a, b) => a.columnIndex - b.columnIndex);
  }
  return grouped;
}

function defaultColumnLabel(cell: CellNode): string {
  return cell.columnDate.toString();
}

function stringifyCellValue(
  cell: CellNode,
  spec: Option<PerTypeSpec>,
): string {
  const content = cell.content;
  if (content == null || content.kind === "none") return "";
  const entry = content.entry;
  if (entry == null) return "";
  const v = entry.value;
  if (spec?.cellValueFormatter) {
    return spec.cellValueFormatter(v ?? null, {
      rowType: spec.id,
      pluginId: spec.id,
    });
  }
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "✓" : "";
  // Complex values (Event, Weather, Task, etc.) — opaque marker.
  return `<${typeof v}>`;
}

/**
 * Builds the `ParsedTree` snapshot of a hydrated `Sheet`, which the fixture tests compare with a
 * fixture's `hydrated` field.
 */
export namespace HydratedTree {
  export const build = buildHydratedTree;
}

/**
 * Builds the rendered-tree snapshot of a `Sheet` (the rows a renderer shows, with their cells),
 * which the CLI prints and the fixture tests compare.
 */
export namespace SceneTree {
  export const build = buildSceneTree;
}
